import React, {
  useEffect,
  useState,
  useContext,
  createContext,
  useCallback,
} from 'react'
import { Form } from '~/lib/ui/form'
import { useReminderForm } from './useReminderForm'
import { getApi } from '~/util/api'
import { DatePickerField } from '~/components/shared/DatePickerField'
import { TextareaField } from '~/components/surveys/TextareaField'
import { Button } from '~/lib/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/lib/ui/dialog'
import SavedStatusIcon from '~/components/shared/SavedStatusIcon'
import { useTranslation } from '../../hooks/useTranslation'
import { TranslationProvider } from '../shared/TranslationProvider'
import translations from './locales.json'

const ReminderDialogContext = createContext()

export const useReminderDialog = () => {
  return useContext(ReminderDialogContext)
}

export const ReminderProvider = ({
  webinarLink,
  candidateId,
  initialText,
  documentId,
  skillName,
  savedCourse,
  children,
  setStatus,
  setReminder,
  locale,
}) => {
  const [showAddReminderDialog, setShowAddReminderDialog] = useState(false)
  const [editingReminder, setEditingReminder] = useState(null)
  const [reminders, setReminders] = useState([])

  const toggleShowAddReminderDialog = () => {
    setShowAddReminderDialog(!showAddReminderDialog)
  }

  const existingReminder = useCallback(
    (responseReminders) => {
      return responseReminders.find(
        (r) =>
          r.content === initialText &&
          r.skill_name === skillName &&
          r.document_id === documentId,
      )
    },
    [initialText, skillName, documentId],
  )

  const getReminders = useCallback(() => {
    getApi()
      .get('/reminders.json?page=1&per_page=5')
      .then((response) => {
        if (response.status == 200) {
          setReminders(response.data.reminders)
          const reminder = existingReminder(response.data.reminders)
          if (reminder) {
            setEditingReminder(reminder)
          }
          return
        } else {
          throw new Error('json request failed')
        }
      })
      .catch((error) => {
        console.log('json request failed', error)
      })
  }, [existingReminder])

  useEffect(() => {
    getReminders()
  }, [getReminders])

  const value = {
    showAddReminderDialog,
    setShowAddReminderDialog,
    toggleShowAddReminderDialog,
    editingReminder,
    setEditingReminder,
    reminders,
    setReminders,
    candidateId,
    initialText,
    documentId,
    skillName,
    webinarLink,
    savedCourse,
    setStatus,
    setReminder,
    locale,
  }

  return (
    <ReminderDialogContext.Provider value={value}>
      {children}
    </ReminderDialogContext.Provider>
  )
}

export const SavedItemReminder = ({ children, ...props }) => {
  return (
    <ReminderProvider {...props}>
      <SavedItemReminderInner>{children}</SavedItemReminderInner>
    </ReminderProvider>
  )
}

const SavedItemReminderInner = ({ children }) => {
  return <ReminderDialog>{children}</ReminderDialog>
}

export const Standalone = ({ ...props }) => {
  const { locale } = props

  return (
    <ReminderProvider {...props}>
      <TranslationProvider
        scope="javascript.components.home"
        locale={locale}
        translations={translations}
      >
        <StandaloneInner />
      </TranslationProvider>
    </ReminderProvider>
  )
}

const StandaloneInner = () => {
  const { t } = useTranslation('reminder_dialog')
  const { toggleShowAddReminderDialog, editingReminder } = useReminderDialog()
  return (
    <ReminderDialog>
      <Button
        className="max-w-[15.5rem] font-roboto"
        onClick={toggleShowAddReminderDialog}
      >
        {editingReminder ? t('edit_reminder') : t('remind_me_about_this_skill')}
      </Button>
    </ReminderDialog>
  )
}

/* CTS-9267: title via DialogTitle (Radix → h2); CTS-9206: dialog close in ~/lib/ui/dialog */
const ReminderDialog = ({ children }) => {
  const { t, locale } = useTranslation('reminder_dialog')
  const {
    showAddReminderDialog,
    toggleShowAddReminderDialog,
    editingReminder,
  } = useReminderDialog()

  return (
    <Dialog
      open={showAddReminderDialog}
      onOpenChange={toggleShowAddReminderDialog}
    >
      <DialogTrigger asChild className="cursor-pointer">
        {children}
      </DialogTrigger>
      <DialogContent
        className="site-info-dialog max-w-xl font-roboto"
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle className="font-roboto-slab text-xl tracking-[0.12px]">
            {t('set_reminder_title')}
          </DialogTitle>
        </DialogHeader>
        {!editingReminder && <AddReminderForm locale={locale} />}
        {editingReminder && <EditReminderForm locale={locale} />}
      </DialogContent>
    </Dialog>
  )
}
export default ReminderDialog

const ReminderForm = ({ candidateId, reminder, locale }) => {
  const { t } = useTranslation('reminder_dialog')
  const { form, onSubmit, submitted, loadingStatus } = useReminderForm({
    candidateId,
    reminder,
    locale,
  })
  const buttonText = loadingStatus === 'saving' ? t('saving') : t('save')

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="mb-4 ml-4 mr-4">
        <DatePickerField
          name="due_date"
          id="reminder_due_date"
          label={t('reminder_due_date')}
          disabled={(date) =>
            date < new Date() || date > new Date('2100-01-01')
          }
          control={form.control}
          pickDate={t('pick_a_date')}
          locale={locale}
        />
        <TextareaField
          name="content"
          id="reminder_content"
          className="w-full"
          label={t('share_your_thoughts')}
          control={form.control}
          placeholder={t('remember_to_placeholder')}
        />
        <div className="flex w-full flex-row justify-end">
          <Button disabled={submitted} type="submit">
            {buttonText}
            <SavedStatusIcon loadingStatus={loadingStatus} />
          </Button>
        </div>
      </form>
    </Form>
  )
}

const AddReminderForm = (props) => {
  const { locale } = props
  const { candidateId } = useReminderDialog()
  return <ReminderForm candidateId={candidateId} locale={locale} />
}

const EditReminderForm = (props) => {
  const { locale } = props
  const { candidateId, editingReminder } = useReminderDialog()

  return (
    <ReminderForm
      candidateId={candidateId}
      reminder={editingReminder}
      locale={locale}
    />
  )
}
